import React from 'react';
import { useGetPdfSnapshotsQuery } from '../redux/api/proposalsApi';
import axios from '../services/axiosInstance';
import { API_BASE_URL } from '../config/environment';

const PdfSnapshotsTable = ({ proposalId }) => {
  const { data: pdfSnapshots = [], isLoading: pdfSnapshotsLoading, error: pdfSnapshotsError } = useGetPdfSnapshotsQuery(proposalId, {
    skip: !proposalId,
  });

  const handleViewSnapshot = async (snapshot) => {
    try {
      // Get the authentication token from localStorage (same as axios uses)
      const token = localStorage.getItem('token');
      
      // Build the full backend URL with authentication token
      const viewUrl = `${API_BASE_URL}proposals/${snapshot.proposal_id}/pdf-snapshots/${snapshot.snapshot_id}/view`;
      const urlWithAuth = token ? `${viewUrl}?token=${token}` : viewUrl;
      
      // Open the PDF directly - the server will set proper Content-Disposition headers
      window.open(urlWithAuth, "_blank", "noopener,noreferrer");
      
    } catch (error) {
      console.error('Error viewing PDF snapshot:', error);
      // Error handling moved to toast notifications
    }
  };

  const handleDownloadSnapshot = async (snapshot) => {
    try {
      // Use axios to download the PDF with proper authentication
      const response = await axios.get(
        `/proposals/${snapshot.proposal_id}/pdf-snapshots/${snapshot.snapshot_id}/download`,
        {
          responseType: 'blob' // Important: treat response as binary data
        }
      );
      
      // Create a blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      // Create a temporary link element and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = `CN_${snapshot.proposal_id}_${snapshot.version_number}.pdf`;
      link.style.display = 'none';
      
      // Add to DOM, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the blob URL
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF snapshot:', error);
      // Error handling moved to toast notifications
    }
  };

  const snapshots = Array.isArray(pdfSnapshots) ? pdfSnapshots : [];

  if (pdfSnapshotsLoading) {
    return (
      <div className="card mb-4">
        <div className="card-header">PDF Version History</div>
        <div className="card-body text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <div className="mt-2 text-muted">Loading PDF versions...</div>
        </div>
      </div>
    );
  }

  if (pdfSnapshotsError) {
    return (
      <div className="card mb-4">
        <div className="card-header">PDF Version History</div>
        <div className="card-body text-center">
          <div className="alert alert-danger mb-0">Error loading PDF versions: {pdfSnapshotsError?.data?.error || 'Unknown error'}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="card mb-4">
      <div className="card-header">PDF Version History</div>
      <div className="card-body p-0">
        {snapshots.length === 0 ? (
          <div className="text-center py-4">
            <div className="text-muted">No PDF versions generated yet.</div>
            <div className="text-secondary small mt-1">
              Generate a PDF to see version history here.
            </div>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-striped table-hover mb-0">
              <thead className="table-light">
                <tr>
                  <th>Version</th>
                  <th>Template</th>
                  <th>Generated By</th>
                  <th>Date &amp; Time</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {snapshots.map((snapshot) => (
                  <tr key={snapshot.snapshot_id}>
                    <td><span className="badge bg-primary">v{snapshot.version_number}</span></td>
                    <td>{snapshot.template_type}</td>
                    <td>{snapshot.generated_by_name || 'Unknown'}</td>
                    <td>{new Date(snapshot.generated_at).toLocaleString()}</td>
                    <td>
                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => handleViewSnapshot(snapshot)}
                          title="View this version"
                        >
                          <i className="fas fa-eye me-1"></i> View
                        </button>
                        <button
                          className="btn btn-sm btn-outline-success"
                          onClick={() => handleDownloadSnapshot(snapshot)}
                          title="Download this version"
                        >
                          <i className="fas fa-download me-1"></i> Download
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PdfSnapshotsTable;
